import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mdComponents = {
  p: ({ children }) => <p className="mb-2.5 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="mb-2.5 list-disc space-y-1 pl-4 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2.5 list-decimal space-y-1 pl-4 last:mb-0">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
      {children}
    </a>
  ),
  h1: ({ children }) => <p className="mb-2 font-bold">{children}</p>,
  h2: ({ children }) => <p className="mb-2 font-bold">{children}</p>,
  h3: ({ children }) => <p className="mb-1.5 font-bold">{children}</p>,
  code: ({ children }) => <code className="bg-neutral-100 px-1 py-0.5 text-[0.85em]">{children}</code>,
};

const GREETING = {
  role: "assistant",
  content: "Hi — I'm the DISJINX assistant. Ask me anything about our automations, plans, or how we can save your team time.",
};

const SUGGESTED_QUESTIONS = [
  "What does DISJINX do?",
  "How much are the plans?",
  "What can you automate for my business?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const [conversationId] = useState(() => {
    let id = localStorage.getItem("disjinx_conversation_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("disjinx_conversation_id", id);
    }
    return id;
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy, open]);

  const sendMessage = async (message) => {
    if (!message || busy) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          conversation_id: conversationId,
          page_url: window.location.href,
          locale: navigator.language,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry — I couldn't respond just now. Please try again, or email disjinx.support@gmail.com." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const send = (e) => {
    e.preventDefault();
    sendMessage(input.trim());
  };

  return (
    <>
      <motion.button
        data-testid="chat-bubble-button"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-neutral-950 text-white shadow-sm transition-colors duration-200 hover:bg-neutral-800"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-50 flex h-[520px] w-[380px] max-w-[calc(100vw-3rem)] flex-col border border-neutral-200 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between bg-neutral-950 px-5 py-4">
              <div>
                <p className="font-display text-sm font-bold text-white">DISJINX Assistant</p>
                <p className="text-xs text-neutral-500">AI-powered · replies in seconds</p>
              </div>
              <span className="h-2 w-2 rounded-full bg-emerald-400" data-testid="chat-status-dot" />
            </div>

            <div ref={scrollRef} data-testid="chat-messages" data-lenis-prevent className="flex-1 space-y-4 overflow-y-auto bg-neutral-50 p-5">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div
                    data-testid={`chat-message-${m.role}`}
                    className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-neutral-950 text-white"
                        : "border border-neutral-200 bg-white text-neutral-800"
                    }`}
                  >
                    {m.role === "assistant" ? (
                      <ReactMarkdown remarkPlugins={[remarkBreaks]} components={mdComponents}>
                        {m.content}
                      </ReactMarkdown>
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start" data-testid="chat-typing-indicator">
                  <div className="flex items-center gap-2 border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Thinking…
                  </div>
                </div>
              )}
              {messages.length === 1 && !busy && (
                <div data-testid="chat-suggestions" className="flex flex-col items-start gap-2 pt-1">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      data-testid={`chat-suggestion-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                      onClick={() => sendMessage(q)}
                      className="border border-neutral-300 bg-white px-3.5 py-2 text-left text-xs font-medium text-neutral-700 transition-colors duration-200 hover:border-neutral-950 hover:text-neutral-950"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={send} className="border-t border-neutral-200 bg-white p-3">
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask a question…"
                  aria-label="Chat message"
                  maxLength={4000}
                  disabled={busy}
                  className="flex-1 border border-neutral-200 bg-white px-3 py-2.5 text-sm outline-none transition-colors duration-200 placeholder:text-neutral-500 focus:border-neutral-950"
                />
                <button
                  type="submit"
                  data-testid="chat-send-button"
                  disabled={busy || !input.trim()}
                  aria-label="Send message"
                  className="flex h-10 w-10 items-center justify-center bg-neutral-950 text-white transition-colors duration-200 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p data-testid="chat-privacy-note" className="mt-2 text-[10px] leading-relaxed text-neutral-500">
                Chats are stored for 90 days. Never share passwords, card details, or other sensitive information.
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
